import type { User } from './api';
import { can, canOpen, homePage } from './permissions';

export type NavigationIcon =
  | 'dashboard' | 'production' | 'notifications' | 'work' | 'scan'
  | 'live' | 'machines' | 'issues' | 'tv' | 'printing' | 'announcements'
  | 'users' | 'departments' | 'materials' | 'audit' | 'history' | 'security';

export type NavigationItem = {
  to: string;
  label: string;
  icon: NavigationIcon;
};

export type NavigationGroup = {
  id: string;
  label: string;
  icon: NavigationIcon;
  items: NavigationItem[];
};

export function buildNavigation(user: User) {
  // Preserve the existing menu eligibility as well as each feature's permission.
  const manager = homePage(user) !== '/worker';
  const personal = can(user, 'sessions.work') || can(user, 'operations.work') || !manager;
  const superadmin = user.roles.includes('SUPERADMIN');
  const visible = (items: (NavigationItem | false)[]) =>
    items.filter((item): item is NavigationItem => !!item && canOpen(user, item.to));

  const shortcuts = visible([
    manager && { to: '/admin/dashboard', label: 'Tổng quan', icon: 'dashboard' },
    ['orders.view', 'warehouse.view', 'operations.work', 'tasks.work', 'settings.view'].some(p => can(user, p))
      && { to: '/production', label: 'Sản xuất & kho', icon: 'production' },
    personal && { to: '/notifications', label: 'Thông báo', icon: 'notifications' },
  ]);

  const groups: NavigationGroup[] = [
    {
      id: 'personal', label: 'Công việc của tôi', icon: 'work',
      items: visible([
        personal && { to: '/worker', label: 'Máy của tôi', icon: 'work' },
        personal && { to: '/scan-machine', label: 'Quét mã máy', icon: 'scan' },
      ]),
    },
    {
      id: 'workshop', label: 'Xưởng & máy', icon: 'machines',
      items: visible([
        manager && { to: '/admin/live', label: 'Theo dõi realtime', icon: 'live' },
        manager && { to: '/admin/machines', label: 'Máy sản xuất', icon: 'machines' },
        manager && { to: '/admin/issues', label: 'Sự cố', icon: 'issues' },
      ]),
    },
    {
      id: 'devices', label: 'TV & thiết bị', icon: 'tv',
      items: visible([
        manager && { to: '/admin/tv', label: 'Kịch bản TV', icon: 'tv' },
        can(user, 'printing.view') && { to: '/printing', label: 'Máy in', icon: 'printing' },
        can(user, 'printing.view') && can(user, 'printing.manage') && { to: '/connection-stations', label: 'Trạm kết nối', icon: 'printing' },
        superadmin && { to: '/admin/esp32', label: 'Nạp firmware ESP32', icon: 'security' },
      ]),
    },
    {
      id: 'administration', label: 'Quản trị', icon: 'users',
      items: visible([
        manager && { to: '/admin/users', label: 'Nhân sự', icon: 'users' },
        manager && { to: '/admin/departments', label: 'Phòng/xưởng', icon: 'departments' },
        manager && can(user, 'materials.view') && { to: '/admin/materials', label: 'Nguyên liệu & công đoạn', icon: 'materials' },
        manager && { to: '/admin/audit', label: 'Nhật ký', icon: 'audit' },
        manager && { to: '/admin/orders', label: 'Lịch sử lệnh cũ', icon: 'history' },
        manager && superadmin && { to: '/admin/roles', label: 'Vai trò và quyền', icon: 'security' },
        manager && superadmin && { to: '/admin/developer', label: 'Nhà phát triển', icon: 'security' },
      ]),
    },
  ];
  return { shortcuts, groups: groups.filter(group => group.items.length > 0) };
}

export function activeNavigationGroup(groups: NavigationGroup[], pathname: string) {
  return groups.find(group => group.items.some(item =>
    pathname === item.to || pathname.startsWith(`${item.to}/`),
  ))?.id ?? null;
}
